/**
 * Percussion, as data.
 *
 * A drum part is the one thing in this project that must NOT be random. Every
 * other layer is generated because a wrong note inside the key is still music;
 * a drum pattern chosen at random is not a groove, it is someone dropping a
 * kit down the stairs. Grooves are therefore written out, and adding one is a
 * line of text rather than a change to the generator.
 *
 * A line reads left to right in sixteenth notes across one bar:
 *
 *   x   hit
 *   X   accent, louder
 *   .   silence
 *
 * Four characters to a beat. The bar is however long the line is, so a
 * twelve-character line is a bar of 12/16, which is how the swung grooves work.
 */

export interface GrooveVoice {
  /** An instrument from DRUMS. */
  drum: string;
  /** Which note to play it at. Retuning a kit is a data change. */
  note: string;
  /** Sixteenths, as described above. */
  steps: string;
  /** Multiplied into the layer volume. */
  gain?: number;
}

export interface Groove {
  name: string;
  /** How many sixteenths make a bar here. 16 unless the groove says otherwise. */
  stepsPerBar?: number;
  voices: GrooveVoice[];
  /**
   * Played instead of `voices` on the last bar of each progression cycle.
   *
   * Without one, a drum part is the same bar repeated for ninety seconds, and
   * that is heard as a loop rather than as playing however good the bar is. A
   * fill is also the clearest signal a listener gets that the piece is about to
   * come round again, which is what stops a long loop feeling directionless.
   */
  fill?: GrooveVoice[];
}

export const GROOVES: Record<string, Groove> = {
  /** Nothing at all. The ambient styles use this and it has to be nameable. */
  none: { name: "none", voices: [] },

  heartbeat: {
    name: "heartbeat",
    voices: [
      { drum: "kick", note: "C1", steps: "X.......x.......", gain: 0.9 },
    ],
  },

  pulse: {
    name: "pulse",
    voices: [
      { drum: "kick", note: "C1", steps: "X...x...X...x..." },
      { drum: "shaker", note: "A5", steps: "..x...x...x...x.", gain: 0.7 },
    ],
  },

  fourFloor: {
    name: "four on the floor",
    voices: [
      { drum: "kick", note: "C1", steps: "X...X...X...X..." },
      { drum: "hat", note: "A5", steps: "..x...x...x...x." },
      { drum: "snare", note: "D3", steps: "....X.......X..." },
    ],
    fill: [
      { drum: "kick", note: "C1", steps: "X...X..........." },
      { drum: "snare", note: "D3", steps: "........x.x.X.xx" },
      { drum: "open_hat", note: "A5", steps: "..............X." },
    ],
  },

  backbeat: {
    name: "backbeat",
    voices: [
      { drum: "kick", note: "C1", steps: "X.....x.X......." },
      { drum: "snare", note: "D3", steps: "....X.......X..." },
      { drum: "hat", note: "A5", steps: "x.x.x.x.x.x.x.x." },
    ],
    fill: [
      { drum: "kick", note: "C1", steps: "X.....x.X......." },
      { drum: "snare", note: "D3", steps: "....X.......x.xx" },
      { drum: "tom", note: "G2", steps: "..........x....." },
    ],
  },

  driving: {
    name: "driving",
    voices: [
      { drum: "kick", note: "C1", steps: "X..x..X...x.X..." },
      { drum: "snare", note: "D3", steps: "....X.......X..." },
      { drum: "hat", note: "A5", steps: "xxxxxxxxxxxxxxxx", gain: 0.55 },
      { drum: "open_hat", note: "A5", steps: "..............x." },
    ],
    fill: [
      { drum: "kick", note: "C1", steps: "X.......X......." },
      { drum: "snare", note: "D3", steps: "....X...x.xxX.xx" },
      { drum: "tom", note: "G2", steps: "............x.x.", gain: 0.8 },
    ],
  },

  drivingOpen: {
    name: "driving, open",
    // Same engine room, different top. Swapping the closed hat for an open one
    // on the offbeats is a bigger change to what a listener hears than any
    // instrument choice elsewhere in the piece.
    voices: [
      { drum: "kick", note: "C1", steps: "X..x..X...x.X..." },
      { drum: "snare", note: "D3", steps: "....X.......X..." },
      { drum: "open_hat", note: "A5", steps: "..x...x...x...x.", gain: 0.5 },
    ],
    fill: [
      { drum: "kick", note: "C1", steps: "X...X..........." },
      { drum: "tom", note: "G2", steps: "........X.x.X.x." },
      { drum: "snare", note: "D3", steps: "....X..........." },
    ],
  },

  drivingBusy: {
    name: "driving, busy",
    voices: [
      { drum: "kick", note: "C1", steps: "X.x...X.x.X...x." },
      { drum: "snare", note: "D3", steps: "....X....x..X..x" },
      { drum: "hat", note: "A5", steps: "x.xxx.xxx.xxx.xx", gain: 0.5 },
    ],
    fill: [
      { drum: "snare", note: "D3", steps: "x.xxx.xxX.xxX.xx" },
      { drum: "kick", note: "C1", steps: "X.......X......." },
    ],
  },

  pushed: {
    name: "pushed",
    // The kick lands a sixteenth early into beats three and four, which is the
    // whole feel: it pulls the bar forward instead of sitting on the grid.
    voices: [
      { drum: "kick", note: "C1", steps: "X......xX.....x." },
      { drum: "snare", note: "D3", steps: "....X.......X..." },
      { drum: "hat", note: "A5", steps: "x.x.x.x.x.x.x.x.", gain: 0.5 },
    ],
    fill: [
      { drum: "kick", note: "C1", steps: "X......xX......." },
      { drum: "tom", note: "G2", steps: "..........x.x.x." },
      { drum: "snare", note: "D3", steps: "....X.......X.x." },
    ],
  },

  halfTime: {
    name: "half time",
    voices: [
      { drum: "kick", note: "C1", steps: "X.......x.x....." },
      { drum: "snare", note: "D3", steps: "........X......." },
      { drum: "hat", note: "A5", steps: "..x...x...x...x." },
    ],
  },

  skip: {
    name: "skip",
    // Twelve sixteenths to the bar, so each beat is a triplet. That single
    // number is the whole difference between this and a straight groove, and
    // it is why stepsPerBar is a property rather than a constant.
    stepsPerBar: 12,
    voices: [
      { drum: "kick", note: "C1", steps: "X..x..X..x.." },
      { drum: "hat", note: "A5", steps: "x.xx.xx.xx.x", gain: 0.6 },
      { drum: "rim", note: "E4", steps: "...X.....X.." },
    ],
  },

  // Quiet grooves. An ambient piece with a backbeat is not a calmer song, it is
  // a different thing nobody asked for, but "no drums at all" was making six
  // moods sound like one. These give a piece a floor without giving it a beat.

  brush: {
    name: "brush",
    voices: [
      { drum: "shaker", note: "A5", steps: "x...x...x...x...", gain: 0.5 },
    ],
  },

  tick: {
    name: "tick",
    voices: [
      { drum: "rim", note: "E4", steps: "........x.......", gain: 0.45 },
      { drum: "shaker", note: "A5", steps: "..x...x...x...x.", gain: 0.3 },
    ],
  },

  breath: {
    name: "breath",
    // Nothing on beat one. The bar starts with a hole, which is what stops
    // this reading as a beat at all and leaves it as movement underneath.
    voices: [
      { drum: "shaker", note: "A5", steps: "......x.......x.", gain: 0.4 },
      { drum: "kick", note: "C1", steps: "....x...........", gain: 0.5 },
    ],
  },

  // Loud ones.

  stomp: {
    name: "stomp",
    voices: [
      { drum: "kick", note: "C1", steps: "X.......X.x.....", gain: 1.1 },
      { drum: "tom", note: "G2", steps: "............X..." },
      { drum: "snare", note: "D3", steps: "........X.......", gain: 0.9 },
    ],
  },

  gallop: {
    name: "gallop",
    voices: [
      { drum: "kick", note: "C1", steps: "X.xxX.xxX.xxX.xx", gain: 0.85 },
      { drum: "snare", note: "D3", steps: "....X.......X..." },
      { drum: "hat", note: "A5", steps: "..x...x...x...x.", gain: 0.5 },
    ],
    fill: [
      { drum: "kick", note: "C1", steps: "X.xxX.xx........" },
      { drum: "tom", note: "G2", steps: "........X.x.X.xx" },
    ],
  },

  offbeat: {
    name: "offbeat",
    voices: [
      { drum: "kick", note: "C1", steps: "X.......X......." },
      { drum: "open_hat", note: "A5", steps: "..X...X...X...X.", gain: 0.55 },
      { drum: "rim", note: "E4", steps: "....x.......x...", gain: 0.6 },
    ],
  },

  scatter: {
    name: "scatter",
    voices: [
      { drum: "kick", note: "C1", steps: "X..x..X..x.X...." },
      { drum: "snare", note: "D3", steps: "....X.....X.X..." },
      { drum: "hat", note: "A5", steps: "x.xxx.xxx.xxx.xx", gain: 0.45 },
    ],
  },

  marching: {
    name: "marching",
    voices: [
      { drum: "kick", note: "C1", steps: "X...x...X...x..." },
      { drum: "snare", note: "D3", steps: "x.xxX...x.xxX..." , gain: 0.7 },
      { drum: "tom", note: "G2", steps: "............x.x." },
    ],
  },
};

export const GROOVE_NAMES = Object.keys(GROOVES);

/** One scheduled drum hit, in beats from the start of the bar. */
export interface DrumHit {
  drum: string;
  note: string;
  offsetBeats: number;
  gain: number;
}

/**
 * Turn a groove into hits for one bar.
 *
 * Accents are louder rather than a different sound, which is what a player
 * does and what stops a repeated pattern reading as a machine.
 */
export function grooveHits(groove: Groove, fill = false): DrumHit[] {
  const stepsPerBar = groove.stepsPerBar ?? 16;
  const beatsPerStep = 4 / stepsPerBar;
  const hits: DrumHit[] = [];
  const voices = fill && groove.fill?.length ? groove.fill : groove.voices;

  for (const voice of voices) {
    for (let step = 0; step < voice.steps.length && step < stepsPerBar; step++) {
      const mark = voice.steps[step];
      if (mark !== "x" && mark !== "X") continue;
      hits.push({
        drum: voice.drum,
        note: voice.note,
        offsetBeats: step * beatsPerStep,
        gain: (voice.gain ?? 1) * (mark === "X" ? 1 : 0.62),
      });
    }
  }

  return hits;
}
